import clsx from "clsx";
import { BookOpen, FileText, ChevronRight, List } from "lucide-react";
import { useEffect, useCallback } from "react";
import Markdown from "react-markdown";
import rehypeHighlight from "rehype-highlight";
import rehypeSlug from "rehype-slug";
import remarkGfm from "remark-gfm";

import { useDocsList, useDoc } from "@/hooks/useDocs";
import { useStore } from "@/lib/store";

import { PageViewHeader } from "./PageViewHeader";

export function DocsView(): React.JSX.Element {
  const activeDocSlug = useStore((state) => state.activeDocSlug);
  const setDocSlug = useStore((state) => state.setDocSlug);
  const docsIndexOpen = useStore((state) => state.docsIndexOpen);
  const openDocsIndex = useStore((state) => state.openDocsIndex);
  const closeDocsIndex = useStore((state) => state.closeDocsIndex);
  const { data: listData, isLoading: listLoading } = useDocsList();
  const { data: docData, isLoading: docLoading } = useDoc(activeDocSlug);

  const docs = listData?.docs ?? [];

  // Auto-select index on mount if nothing is selected
  useEffect(() => {
    if (!activeDocSlug && docs.length > 0) {
      const index = docs.find((d) => d.slug === "index");
      setDocSlug(index ? "index" : docs[0].slug);
    }
  }, [activeDocSlug, docs, setDocSlug]);

  // Scroll to hash anchor after doc content loads
  useEffect(() => {
    if (!docData) return;
    const hash = window.location.hash;
    if (!hash) return;
    // Small delay to let react-markdown render + rehype-slug add IDs
    const timer = setTimeout(() => {
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }, 100);
    return () => clearTimeout(timer);
  }, [docData]);

  // Check if a href is an internal doc link (pure, no side effects)
  const isInternalLink = useCallback(
    (href: string): boolean => {
      if (!href.endsWith(".md")) return false;
      const slug = href.replace(/\.md$/, "");
      return docs.some((d) => d.slug === slug);
    },
    [docs],
  );

  // Navigate to an internal doc (side effect, only call from event handlers)
  const navigateToDoc = useCallback(
    (href: string) => {
      const slug = href.replace(/\.md$/, "");
      setDocSlug(slug);
    },
    [setDocSlug],
  );

  const handleSelectDoc = useCallback(
    (slug: string) => {
      setDocSlug(slug);
      closeDocsIndex();
    },
    [setDocSlug, closeDocsIndex],
  );

  return (
    <div className="flex min-h-0 flex-1">
      {/* Mobile backdrop for docs index */}
      <button
        type="button"
        aria-label="Close documentation index"
        className={clsx("docs-index-backdrop md:hidden", docsIndexOpen && "open")}
        onClick={closeDocsIndex}
      />

      {/* Doc sidebar */}
      <div
        className={clsx(
          "docs-index border-cyber-border bg-cyber-bg w-56 flex-shrink-0 overflow-y-auto border-r",
          docsIndexOpen && "open",
        )}
      >
        <div className="border-cyber-border border-b px-4 py-[13px]">
          <div className="flex items-center gap-2">
            <BookOpen size={14} className="text-cyber-cyan" />
            <h2 className="text-cyber-text text-sm font-medium">Docs</h2>
          </div>
        </div>

        {listLoading ? (
          <div className="text-cyber-muted px-4 py-6 text-center text-xs">
            <span className="animate-spin-glow mr-1 inline-block">&#9680;</span>
            Loading...
          </div>
        ) : (
          <nav className="py-2">
            {docs.map((doc) => (
              <button
                key={doc.slug}
                onClick={() => handleSelectDoc(doc.slug)}
                className={clsx(
                  "flex w-full items-center gap-2 px-4 py-2 text-left text-xs transition-all",
                  activeDocSlug === doc.slug
                    ? "border-cyber-cyan bg-cyber-cyan/10 text-cyber-cyan border-r-2"
                    : "text-cyber-muted hover:bg-cyber-surface/50 hover:text-cyber-text",
                )}
              >
                {doc.slug === "index" ? <BookOpen size={12} /> : <FileText size={12} />}
                <span className="truncate">{doc.title}</span>
              </button>
            ))}
          </nav>
        )}
      </div>

      {/* Doc content */}
      <div className="flex min-h-0 flex-1 flex-col">
        <PageViewHeader title="Docs" />

        <div className="flex-1 overflow-y-auto">
          {/* Mobile contents toggle */}
          <button
            className="border-cyber-border text-cyber-muted hover:text-cyber-cyan flex w-full items-center gap-1.5 border-b px-4 py-2 text-xs transition-colors md:hidden"
            onClick={openDocsIndex}
          >
            <List size={12} />
            <span>Contents</span>
          </button>

          {docLoading ? (
            <div className="text-cyber-muted flex items-center justify-center py-20">
              <span className="animate-spin-glow mr-2 inline-block">&#9680;</span>
              Loading doc...
            </div>
          ) : docData ? (
            <div className="mx-auto max-w-3xl px-4 py-6 md:px-8">
              {/* Breadcrumb */}
              <div className="text-cyber-muted/60 mb-4 flex items-center gap-1 text-[10px]">
                <span>Docs</span>
                <ChevronRight size={10} />
                <span className="text-cyber-text/80">{docData.title}</span>
              </div>

              {/* Rendered markdown */}
              <div className="docs-prose prose prose-sm prose-headings:my-3 prose-p:my-2 prose-pre:my-2 prose-ol:my-2 prose-ul:my-2 prose-li:my-0 prose-table:text-xs max-w-none overflow-x-auto">
                <Markdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeSlug, rehypeHighlight]}
                  components={{
                    a: ({ href, children, ...props }) => {
                      if (href && isInternalLink(href)) {
                        return (
                          <button
                            onClick={() => navigateToDoc(href)}
                            className="text-cyber-cyan cursor-pointer hover:underline"
                            {...(props as React.ButtonHTMLAttributes<HTMLButtonElement>)}
                          >
                            {children}
                          </button>
                        );
                      }
                      return (
                        <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
                          {children}
                        </a>
                      );
                    },
                  }}
                >
                  {docData.content}
                </Markdown>
              </div>
            </div>
          ) : (
            <div className="text-cyber-muted flex flex-col items-center justify-center py-20">
              <BookOpen size={32} className="mb-3 opacity-20" />
              <p className="text-sm">Select a document</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
